import React from 'react';

import { JobProgressUtils, type JobStatus } from '@personal-trainer-app/shared';

import { LinearProgress } from '../Progress';

import type { JobProgressBarProps } from './JobProgressBar.types';
import type { ProgressVariant } from '../Progress';

/**
 * JobProgressBar — a thin, design-system-compliant view over `LinearProgress`
 * for a tracked job. Drives the bar colour from job status (failed → error,
 * completed → success, else the sakura primary), shows an indeterminate bar
 * until the first real percentage, and renders the step message + percentage.
 *
 * Presentational only: pass a `progress` snapshot (e.g. from `useJobProgress`).
 */
const STATUS_VARIANT: Record<JobStatus, ProgressVariant> = {
  pending: 'primary',
  started: 'primary',
  processing: 'primary',
  completed: 'success',
  failed: 'error',
  // Waiting on a person, not broken: it keeps the in-flight colour.
  held: 'primary',
};

export const JobProgressBar: React.FC<JobProgressBarProps> = ({
  progress,
  value,
  status,
  message,
  showMessage = true,
  showPercentage = true,
  size = 'md',
  'aria-label': ariaLabel,
  className = '',
}) => {
  const resolvedStatus: JobStatus = status ?? progress?.status ?? 'pending';
  const resolvedPercentage = Math.round(value ?? progress?.percentage ?? 0);
  const resolvedMessage = message ?? progress?.message ?? null;

  const isTerminal = JobProgressUtils.isTerminalJobStatus(resolvedStatus);
  // No real percentage yet — show the indeterminate sweep instead of a 0% bar.
  const isIndeterminate = !isTerminal && resolvedPercentage <= 0;
  const variant = STATUS_VARIANT[resolvedStatus];

  const captionColor =
    resolvedStatus === 'failed' ? 'text-error-400' : 'text-neutral-400';

  const showCaptionRow = showMessage || (showPercentage && !isIndeterminate);

  return (
    <div className={className}>
      <LinearProgress
        variant={variant}
        size={size}
        value={isIndeterminate ? undefined : resolvedPercentage}
        aria-label={ariaLabel ?? 'Job progress'}
      />
      {showCaptionRow && (
        <div className="mt-2 flex items-center justify-between gap-3">
          {showMessage && (
            <span className={`text-body-sm ${captionColor}`}>
              {resolvedMessage ?? ' '}
            </span>
          )}
          {showPercentage && !isIndeterminate && (
            <span className="ml-auto font-mono text-label-sm text-primary-300">
              {resolvedPercentage}%
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default JobProgressBar;
